import { Switch } from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import React from 'react';

import { currencies } from '~/src/dash/api/exchange-rates';
import { getSimpleSingleOptionSelects, ISimpleOption } from '~/src/dash/components/common';
import { SimpleSingleSelect } from '~/src/dash/components/dropdowns/SimpleSingleSelect';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { spacingV2 } from '~/src/dash/components/layout/common';
import { getLineHeight } from '~/src/dash/components/text/common';
import { timezones } from '~/src/dash/globals/timezone';
import { borderRadius, Col, Cols, Row, Rows, Text, TextInput } from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { getReportFrequencyStr, getReportPeriodStr } from '~/src/dash/views/reporting/common';

interface IOverviewSectionProps {
  name: string;
  setName: (name: string) => void;
  timezone: string;
  isTimezoneOptionDisabled: (opt: ISimpleOption) => boolean;
  setTimezone: (tz: string) => void;
  currency: string;
  setCurrency: (currency: string) => void;
  isSuperUserOnly: boolean;
  setIsSuperUserOnly: (superUserOnly: boolean) => void;
  isLoading: boolean;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  period: string;
  frequency: string;
  isSchedulingActive: boolean;
  enableTimezone?: boolean;
  enableCurrency?: boolean;
}
export const OverviewSection = ({
  name,
  setName,
  timezone,
  isTimezoneOptionDisabled,
  setTimezone,
  currency,
  setCurrency,
  isSuperUserOnly,
  setIsSuperUserOnly,
  isLoading,
  createdAt,
  createdBy,
  updatedAt,
  updatedBy,
  period,
  frequency,
  isSchedulingActive,
  enableTimezone,
  enableCurrency,
}: IOverviewSectionProps) => {
  const { isSuperUser } = useBaseStore();

  return (
    <Rows>
      <Row>
        <Cols gap={spacingV2.medium}>
          <Col displayFlex style={{ alignItems: 'center' }}>
            <IconV2 name='cube' />
          </Col>
          <Col>
            <Text sizeV2 bold large>
              Overview
            </Text>
          </Col>
        </Cols>
      </Row>

      {!isLoading ? (
        <Row noVerticalPaddingV2>
          <Cols
            wrap
            gap={spacingV2.small}
            extraLargeHorizontalPaddingV2
            style={{ justifyContent: 'space-between' }}
          >
            <Col>
              <Rows>
                <Row noPadding marginBottom={spacingV2.small}>
                  <Cols style={{ alignItems: 'center' }}>
                    <Col width='7.5rem'>
                      <Text sizeV2 small bold>
                        Report Name
                      </Text>
                    </Col>
                    <Col>
                      <TextInput
                        width='18.75rem'
                        inputSize='small'
                        placeholder='Report Name'
                        initialValue={name}
                        handleOnChange={(input) => setName(input)}
                      />
                    </Col>
                  </Cols>
                </Row>

                {isSuperUser && (
                  <Row noPadding marginBottom={spacingV2.small}>
                    <Cols style={{ alignItems: 'center' }}>
                      <Col width='7.5rem'>
                        <Text sizeV2 small bold>
                          Permission *
                        </Text>
                      </Col>
                      <Col>
                        <Switch
                          name='SuperUserOnly'
                          value={isSuperUserOnly}
                          checked={isSuperUserOnly}
                          onChange={() => setIsSuperUserOnly(!isSuperUserOnly)}
                        ></Switch>
                      </Col>
                      <Col>
                        <Text sizeV2 small>
                          Restricted To Super Admin
                        </Text>
                      </Col>
                    </Cols>
                  </Row>
                )}
                {enableTimezone && (
                  <Row noPadding marginBottom={spacingV2.small}>
                    <Cols>
                      <Col width={120} style={{ display: 'flex', alignItems: 'center' }}>
                        <Text small bold>
                          Timezone
                        </Text>
                      </Col>
                      <Col>
                        <SimpleSingleSelect
                          inputWidth='12.5rem'
                          options={getSimpleSingleOptionSelects(
                            timezones,
                            timezone,
                            isTimezoneOptionDisabled,
                          )}
                          onChange={(id) => setTimezone(id)}
                          isSearchEnabled
                          isSortEnabled
                          popoverWidth='12.5rem'
                        />
                      </Col>
                    </Cols>
                  </Row>
                )}
                {enableCurrency && (
                  <Row noPadding marginBottom={spacingV2.small}>
                    <Cols>
                      <Col width={120} style={{ display: 'flex', alignItems: 'center' }}>
                        <Text small bold>
                          Currency
                        </Text>
                      </Col>
                      <Col>
                        <SimpleSingleSelect
                          inputWidth='12.5rem'
                          options={getSimpleSingleOptionSelects(currencies, currency)}
                          onChange={(id) => setCurrency(id)}
                          isSortEnabled
                          isSearchEnabled
                          popoverWidth='12.5rem'
                        />
                      </Col>
                    </Cols>
                  </Row>
                )}
              </Rows>
            </Col>

            <Col>
              <Cols
                style={{
                  background: `${publicaColors.veryLightGray}`,
                  borderRadius: borderRadius.small,
                }}
              >
                <Col>
                  <Rows extraSmallPaddingV2>
                    <Row extraSmallVerticalPaddingV2>
                      <Text sizeV2 bold small>
                        Created By
                      </Text>
                    </Row>
                    <Row extraSmallVerticalPaddingV2>
                      <Text sizeV2 bold small>
                        Last Modified
                      </Text>
                    </Row>
                    <Row extraSmallVerticalPaddingV2>
                      <Text
                        sizeV2
                        bold
                        small
                        style={{
                          display: 'inline-block',
                          borderRadius: borderRadius.small,
                          backgroundColor: isSchedulingActive
                            ? publicaColors.honeydew
                            : publicaColors.lightGray,
                          padding: '0.25rem',
                        }}
                      >
                        {isSchedulingActive ? 'Scheduled' : 'Unscheduled'}
                      </Text>
                    </Row>
                  </Rows>
                </Col>

                <Col>
                  <Rows extraSmallPaddingV2>
                    <Row extraSmallVerticalPaddingV2>
                      <Text sizeV2 small>
                        {createdBy || 'N/A'}
                      </Text>
                    </Row>
                    <Row extraSmallVerticalPaddingV2>
                      <Text sizeV2 small>
                        {updatedBy || 'N/A'}
                      </Text>
                    </Row>
                    <Row extraSmallVerticalPaddingV2>
                      <Text sizeV2 small>
                        {isSchedulingActive
                          ? `${getReportPeriodStr(+period)}, ${getReportFrequencyStr(+frequency)}`
                          : 'Set up schedule below'}
                      </Text>
                    </Row>
                  </Rows>
                </Col>

                <Col>
                  <Rows extraSmallPaddingV2>
                    <Row extraSmallVerticalPaddingV2>
                      <Text sizeV2 small>
                        {createdAt || 'N/A'}
                      </Text>
                    </Row>
                    <Row extraSmallVerticalPaddingV2>
                      <Text sizeV2 small>
                        {updatedAt || 'N/A'}
                      </Text>
                    </Row>
                  </Rows>
                </Col>
              </Cols>
            </Col>
          </Cols>
        </Row>
      ) : (
        <Skeleton
          variant='text'
          animation='wave'
          width='100%'
          height={getLineHeight('extraLarge')}
        />
      )}
    </Rows>
  );
};
